import { Injectable } from '@angular/core';
import { Path } from '@shared-enums';
import { SideNavigationItem, SideNavigationSubcategory } from '@shared-models';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SideNavigationService {
  //#region Properties

  /**
   * Currently selected item from the side navigation
   * that will be displayed inside a header.
   */
  public activeSubcategory$ = new BehaviorSubject<SideNavigationSubcategory>({
    active: true,
    parent: 'Navigation.BicycleRental',
    path: Path.Bicycles,
    title: 'Navigation.Bicycles',
  });

  //#endregion

  //#region Methods

  /**
   * Define and return side navigation items
   * containing category titles, icons, and subcategories.
   *
   * @returns SIdeNavigation[]
   */
  getSideNavigationItems(): SideNavigationItem[] {
    return [
      {
        category: 'Navigation.BicycleRental',
        icon: 'directions_bike',
        expanded: false,
        subcategories: this.generateSubcategories('Navigation.BicycleRental', [
          'Bicycles',
          'AdditionalGear',
          'CyclingTours',
          'CyclingPaths',
          'GPSLink',
        ]),
      },
      {
        category: 'Navigation.SunbedRental',
        icon: 'deck',
        expanded: false,
        subcategories: this.generateSubcategories('Navigation.SunbedRental', [
          'MicroLocations',
          'Zones',
          'SunbedTypes',
        ]),
      },
      {
        category: 'Navigation.Articles',
        icon: 'loyalty',
        expanded: false,
        subcategories: this.generateSubcategories('Navigation.Articles', [
          'ArticleList',
          'ArticleCategories',
          'ArticlePrices',
        ]),
      },
      {
        category: 'Navigation.Codebooks',
        icon: 'folder',
        expanded: false,
        subcategories: this.generateSubcategories('Navigation.Codebooks', [
          'Pedals',
          'BikeCategories',
          'ReservationDuration',
          'WorkingHours',
          'RentalDuration',
          'PriceList',
          'GearPriceList',
          'DocumentTypes',
          'RentalNote',
        ]),
      },
      {
        category: 'Navigation.CodebooksForSunbeds',
        icon: 'folder',
        expanded: false,
        subcategories: this.generateSubcategories('Navigation.CodebooksForSunbeds', [
          'RentalDurationSunbeds',
          'ReservationDurationSunbeds',
          'PriceListSunbeds',
        ]),
      },
      {
        category: 'Navigation.PaymentFooter',
        icon: 'point_of_sale',
        expanded: false,
        subcategories: this.generateSubcategories('Navigation.PaymentFooter', [
          'AllPaymentFooters',
        ]),
      },
      {
        category: 'Navigation.SEO',
        icon: 'public',
        expanded: false,
        subcategories: this.generateSubcategories('Navigation.SEO', [
          'SearchEngineOptimization',
        ]),
      },
      {
        category: 'Navigation.UserAccounts',
        icon: 'manage_accounts',
        expanded: false,
        subcategories: this.generateSubcategories('Navigation.UserAccounts', [
          'AccountManagement',
        ]),
      },
    ];
  }

  /**
   * Generate a list of side navigation subcategories
   * for their names given. Parent represents the
   * category containing all subcategories generated here.
   *
   * Each subcategory must be added to Path enum with exactly same value.
   * For example subcategory X is added, there must be Path.X entry.
   * Also, nn the translation files, generate Navigation.X entry as well.
   *
   *
   * @param subcategories string[]
   * @param parent: string;
   *
   * @returns SideNavigationSubcategory[]
   */
  private generateSubcategories(
    parent: string,
    subcategories: string[],
  ): SideNavigationSubcategory[] {
    return subcategories.map(subcategory => ({
      title: `Navigation.${subcategory}`,
      path: Path[subcategory as keyof typeof Path],
      active: false,
      parent,
    }));
  }

  //#endregion
}
